import { useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { getAllPapers } from '@/lib/api/paper-queries/getAllPapers';
import { PenLine, Sparkles } from 'lucide-react';

const NavLinks = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Prefetch Explore data when user hovers the link for 100ms
  const handleExploreMouseEnter = useCallback(() => {
    prefetchTimeoutRef.current = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['explore-papers', { sortBy: 'updated_at', offset: 0, limit: 20 }],
        queryFn: () => getAllPapers({ sortBy: 'updated_at', offset: 0, limit: 20 }),
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    }, 100);
  }, [queryClient]);
  
  const handleExploreMouseLeave = useCallback(() => {
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = null;
    }
  }, []);

  const linkClasses = (active: boolean) =>
    cn(
      "px-3 py-2 text-sm font-medium transition-all rounded-lg",
      active ? "text-primary bg-white/60" : "text-foreground/70 hover:text-foreground hover:bg-white/50"
    );

  return (
    <nav className="hidden md:flex items-center space-x-4">
      <Link
        to="/write"
        className={cn(
          linkClasses(location.pathname === "/write" || location.pathname.startsWith("/write/")),
          "inline-flex items-center gap-1.5"
        )}
      >
        <PenLine className="h-4 w-4" />
        Write
      </Link>
      <Link
        to="/upload"
        className={cn(
          linkClasses(location.pathname === "/upload"),
          "inline-flex items-center gap-1.5"
        )}
      >
        <Sparkles className="h-4 w-4" />
        Review My Paper
      </Link>
      <Link
        to="/explore"
        className={linkClasses(location.pathname === "/explore")}
        onMouseEnter={handleExploreMouseEnter}
        onMouseLeave={handleExploreMouseLeave}
      >
        Explore
      </Link>
      <Link 
        to="/pricing" 
        className={linkClasses(location.pathname === "/pricing")}
      >
        Pricing
      </Link>
      <Link 
        to="/about" 
        className={linkClasses(location.pathname === "/about")}
      >
        About
      </Link>
    </nav>
  );
};

export default NavLinks;
